/**
 * What the owner reads about a profile change the agent sent to Google.
 *
 * Shardul, QA, 23 Sep 26 (note 3e90e103, Digimatrix): the Activity line at
 * 12:36 read "Done: Set the phone number to 7898624979. Your phone number is
 * live on Google." and the same card's detail read "Phone number, Pending at
 * Google. Sent 23 Sep 26, 12:36." Two statements about one change at the same
 * minute, and the one that claimed live was the one that could not know: the
 * verification record is only seeded at the write, and Google has silently
 * dropped an accepted phone number before (Mahajan Brothers).
 *
 * So the sentence is built here, from one table of field words and one set of
 * standing sentences, and the standing comes from the verification record
 * rather than from the write's own read-back. Only the 'live' standing may
 * say "Live on", and only the verify pass can set it.
 *
 * Anand, 25 Sep 26 (Zerebral IT Solutions): the line names where the change
 * can be seen, Google's record or the public listing or the owner's Fly
 * website, and says done only once it is there.
 *
 * Pure, and twinned with supabase/functions/agent-action-respond/change-wording.ts
 * so the Activity list (worker) and the detail dialog (edge) say the same
 * thing. Guarded by tests/agent/unit/done-line-pending.test.ts.
 */
import { nameList, namesFromExpected } from './category-lines.js';

// ---- shared logic: keep byte-identical with the twin ----

/**
 * How the value reads inside the sentence:
 *   inline  the words then the value, "phone number 7898624979"
 *   alone   the value already carries its own words, "3 attributes"
 *   none    the value is too long for one line, "your new business description"
 *
 * where is the tab of Edit profile on Google that holds the detail, which is
 * where the owner can see a change the public listing does not carry.
 */
export type ValueShape = 'inline' | 'alone' | 'none';

/** Every bridge that writes a profile detail on Google, and the owner's words for it. */
export const CHANGE_FIELDS: Record<string, { words: string; value: ValueShape; where: string }> = {
  phone: { words: 'phone number', value: 'inline', where: 'Contact' },
  website: { words: 'website link', value: 'inline', where: 'Contact' },
  description: { words: 'business description', value: 'none', where: 'About' },
  name: { words: 'business name', value: 'inline', where: 'About' },
  primary_category_publish: { words: 'primary category', value: 'inline', where: 'About' },
  categories_publish: { words: 'additional categories', value: 'inline', where: 'About' },
  categories_remove_publish: { words: 'additional categories', value: 'inline', where: 'About' },
  attributes_publish: { words: 'attributes', value: 'alone', where: 'More' },
  services_publish: { words: 'services', value: 'inline', where: 'Services' },
  special_hours_publish: { words: 'special hours', value: 'inline', where: 'Hours' },
  regular_hours_publish: { words: 'opening hours', value: 'inline', where: 'Hours' },
  opening_date_publish: { words: 'opening date', value: 'inline', where: 'About' },
  // The booking link behind the Book button, an APPOINTMENT place action link (booking-on-google.ts, 12 Oct 26).
  booking_link_publish: { words: 'booking link', value: 'inline', where: 'Bookings' },
  // Cards written before 19 Sep 26 carry the bridge name without the suffix.
  opening_date: { words: 'opening date', value: 'inline', where: 'About' },
};

/**
 * The details the agent checks on the public listing itself (the Places read
 * in public-listing.ts). Everything else is only ever confirmed on Google's
 * own record, which the owner sees in Edit profile.
 */
export const PUBLIC_CHECKED_BRIDGES = ['phone', 'website', 'name'];

export function isTrackedChange(bridge: unknown): boolean {
  return Object.prototype.hasOwnProperty.call(CHANGE_FIELDS, String(bridge ?? ''));
}

/**
 * Where a change stands when the sentence is written.
 *   sent            the write went through, nothing read back yet
 *   echoed          Google handed the new value straight back on the write
 *   pending_review  Google has it in its own review queue
 *   live            the verify pass read the profile back and saw it
 *   not_live        48 hours on and Google still shows the old value
 *   replaced        a later edit to the same detail is live instead
 *   removed         the detail is not on the profile any more and nothing is
 *                   pending at Google for it (profile-updates.ts
 *                   removedFromProfile; QA note dcc6e136, 28 Sep 26)
 */
export type SendState = 'sent' | 'echoed' | 'pending_review' | 'live' | 'not_live' | 'replaced' | 'removed';

/**
 * The standing of a change, read from the verification record first and from
 * the write's own read-back only while there is no record yet. A read-back
 * that matched is NOT live: Google echoes a value it can still drop.
 */
export function sendState(result: unknown): SendState {
  const r = (result ?? {}) as Record<string, unknown>;
  const v = (r.verification ?? null) as Record<string, unknown> | null;
  const status = v && typeof v === 'object' ? String(v.status ?? '') : '';
  if (status === 'live') return 'live';
  // The verify pass saw Google hold it for review (profile-updates.ts, QA
  // c17290a9): waiting for Google to approve it, however long that takes.
  if (v?.pending_review === true && (status === 'pending' || status === 'stale')) return 'pending_review';
  if (status === 'stale') return 'not_live';
  if (status === 'replaced') return 'replaced';
  // Settled and terminal, like replaced: what the write's own read-back said
  // at the time is no longer the news (Pradeep food stall, 28 Sep 26: the
  // website had come off the profile and every line still said "Sent to
  // Google, not showing on your profile yet").
  if (status === 'removed') return 'removed';
  if (r.pending_review === true) return 'pending_review';
  if (r.landed === true) return 'echoed';
  return 'sent';
}

/**
 * The owner is told one of three things about a change the agent made, and
 * only the one that is true (Anand, 25 Sep 26, Zerebral IT Solutions: "it
 * said it added services, but I could not see them on the Google profile").
 * The services were on Google's own record and the owner was looking at the
 * public listing, which does not show them, while every line said "live on
 * Google" as if the two were the same place.
 */
export const SENT_NOT_SHOWING = 'Sent to Google, not showing on your profile yet';
/** Google has it and holds it for review before the profile shows it (QA c17290a9, Shardul, 09 Oct 26). */
export const SENT_AWAITING_APPROVAL = 'Sent to Google, waiting for Google to approve it';
/**
 * The booking link's own words until Google lists it with nothing held for
 * review (Anand, 12 Oct 26: the card says "Sent to Google, waiting for
 * Google's review", "Live on Google", or "Google refused" and why).
 */
export const BOOKING_AWAITING_REVIEW = "Sent to Google, waiting for Google's review";
export const BOOKING_LIVE_TAIL = 'Google lists it as your booking link.';
export const LIVE_SERVICES = 'Live on Google, shown in Edit profile under Services, not on the public listing';
export const LIVE_ON_WEBSITE = 'Live on your website';
/**
 * A change whose value is not on the profile any more. The same words the
 * Done card uses (src/components/gbp-agent/doneChangeRecord.ts), so the
 * Activity line, the detail, the moves and the console all say one thing.
 */
export const NOT_ON_PROFILE = 'Not on your Google profile any more';
/** The first words of an Activity title for such a change, as the verify pass titles its own line. */
export const NO_LONGER_ON_GOOGLE = 'No longer on Google';
/**
 * What a card that took a detail off the profile records as its value
 * (profile-updates.ts seedVerification, the Profile page's Remove).
 */
export const CLEARED_EXPECTED = 'Removed from your profile';

/** The verification record fields the standing reads. */
export interface StandingRecord {
  public_confirmed_at?: string | null;
}

/**
 * A standing in two parts: the lead says where the change is and names the
 * place the owner can see it; the tail says what happens next.
 */
export function changeStanding(state: SendState, bridge = '', v: StandingRecord | null = null): { lead: string; tail: string } {
  // The booking link (place action link): Google gives no word on the link itself, so it waits for Google's review until it is live.
  if (bridge === 'booking_link_publish' && (state === 'sent' || state === 'echoed' || state === 'pending_review' || state === 'live')) {
    if (state === 'live') return { lead: 'Live on Google', tail: BOOKING_LIVE_TAIL };
    return { lead: BOOKING_AWAITING_REVIEW, tail: 'Google can take a few days. The agent checks on every pass and tells you once Google shows it.' };
  }
  switch (state) {
    case 'live': {
      if (bridge === 'services_publish') {
        return { lead: LIVE_SERVICES, tail: 'Google decides whether search shows services for your kind of business.' };
      }
      const spec = CHANGE_FIELDS[bridge];
      // A record that does not say which detail it was: live, and no more.
      if (!spec) return { lead: 'Live on Google', tail: '' };
      const where = spec.where;
      if (PUBLIC_CHECKED_BRIDGES.includes(bridge)) {
        return v?.public_confirmed_at
          ? { lead: 'Live on Google, a customer can see it on your public listing', tail: '' }
          : { lead: `Live on Google, shown in Edit profile under ${where}`, tail: 'The agent has not seen it on your public listing yet and keeps checking.' };
      }
      return { lead: `Live on Google, shown in Edit profile under ${where}`, tail: 'The agent cannot check your public listing for it.' };
    }
    case 'pending_review':
      return { lead: SENT_AWAITING_APPROVAL, tail: 'Google has it under review, which can take a few days. The agent keeps checking and tells you when it shows.' };
    case 'not_live':
      return { lead: SENT_NOT_SHOWING, tail: 'It is over 48 hours; the agent keeps checking and says so if Google drops it.' };
    case 'replaced':
      return { lead: 'Replaced on Google by a later edit', tail: 'This one is not waiting on anybody.' };
    case 'removed':
      return { lead: NOT_ON_PROFILE, tail: 'Nothing is waiting on Google for it.' };
    default:
      return { lead: SENT_NOT_SHOWING, tail: 'The agent checks on every pass and tells you when it shows.' };
  }
}

/** The standing as whole sentences, for a note or a chip that stands alone. */
export function changeStandingLine(state: SendState, bridge = '', v: StandingRecord | null = null): string {
  const { lead, tail } = changeStanding(state, bridge, v);
  return `${lead}.${tail ? ` ${tail}` : ''}`;
}

/** Long values are cut here so the line stays one readable sentence. */
function short(value: string): string {
  const v = value.replace(/\s+/g, ' ').trim();
  return v.length > 90 ? `${v.slice(0, 90).trimEnd()}...` : v;
}

/** What the change was, without a verb: "phone number 7898624979", "3 attributes". */
export function changeWhat(bridge: string, value: string): string {
  const spec = CHANGE_FIELDS[bridge];
  if (!spec) return 'the change';
  // A removal names what came off, never "website link Removed from your profile".
  if (String(value ?? '').trim() === CLEARED_EXPECTED) return `your ${spec.words}, taken off your profile`;
  // A category change names the categories and says which way it went
  // (Shardul, QA, 22 Sep 26).
  if (bridge === 'categories_publish') {
    const list = nameList(namesFromExpected(value));
    return list ? `${list} added to your categories` : 'the categories you picked, added to your categories';
  }
  if (bridge === 'categories_remove_publish') {
    const list = nameList(namesFromExpected(value));
    return list ? `${list} taken off your categories` : 'that category, taken off your categories';
  }
  const v = short(String(value ?? ''));
  if (spec.value === 'none') return `your new ${spec.words}`;
  // A bridge that could not name what it sent falls back to the field's own
  // words ("special hours"), which must not be printed twice.
  if (!v || v.toLowerCase() === spec.words) return `your ${spec.words}`;
  if (spec.value === 'alone') return v;
  return `${spec.words} ${v}`;
}

/** The whole line for a tracked change: where it stands, what it was, what happens next. */
export function changeSentLine(bridge: string, value: string, state: SendState, v: StandingRecord | null = null): string {
  const { lead, tail } = changeStanding(state, bridge, v);
  return `${lead}: ${changeWhat(bridge, value)}.${tail ? ` ${tail}` : ''}`;
}

/** The value a tracked bridge sent, as the owner would read it back. */
export function sentValue(result: unknown, bridge: string): string {
  const r = (result ?? {}) as Record<string, unknown>;
  const v = (r.verification ?? null) as Record<string, unknown> | null;
  // The categories the bridge reports come first: the verification summary
  // falls back to "2 categories" when the card carried no labels, and the
  // owner's line has to name them (Shardul, QA, 22 Sep 26).
  if (bridge === 'categories_remove_publish' && typeof r.removed_name === 'string' && r.removed_name) return r.removed_name;
  if (bridge === 'categories_publish' && Array.isArray(r.added_names) && r.added_names.length) return r.added_names.map(String).join(', ');
  if (v && typeof v.expected === 'string' && v.expected) return v.expected;
  for (const key of ['applied_value', 'applied_hours', 'applied_opening_date']) {
    const raw = r[key];
    if (typeof raw === 'string' && raw) return raw;
  }
  return '';
}

/**
 * The Done line for a recorded profile change, or null when the bridge does
 * not write a tracked detail on Google. Both runtimes build it the same way:
 * the worker when the card executes (the Activity line), the edge when the
 * detail dialog opens, so a line written hours ago never contradicts the chip
 * beside it.
 */
export function changeDoneLine(bridge: string, result: unknown): string | null {
  if (!isTrackedChange(bridge)) return null;
  const v = ((result ?? {}) as Record<string, unknown>).verification as StandingRecord | null | undefined;
  return changeSentLine(bridge, sentValue(result, bridge), sendState(result), v ?? null);
}

/**
 * A change to the owner's Fly website is live only when the published page
 * shows it (site-live.ts): the record is execution_result.site_check.
 *   live      the page the owner opens was fetched after the change and carries it
 *   pending   the change is saved; the page still serves the version before it
 *   not_live  the page still showed the old version when the agent gave up waiting
 */
export type SiteState = 'live' | 'pending' | 'not_live';

export function siteState(result: unknown): SiteState | null {
  const check = ((result ?? {}) as Record<string, unknown>).site_check as Record<string, unknown> | null | undefined;
  if (!check || typeof check !== 'object') return null;
  const status = String(check.status ?? '');
  return status === 'live' || status === 'pending' || status === 'not_live' ? status : null;
}

/** The website address as the owner reads it, without the scheme. */
export function siteHost(url: unknown): string {
  return String(url ?? '').replace(/^https?:\/\//, '').replace(/[?#].*$/, '').replace(/\/+$/, '');
}

/** Where a website change stands, in the owner's words. */
export function siteStandingLine(state: SiteState, url: unknown): string {
  const host = siteHost(url);
  switch (state) {
    case 'live':
      return `${LIVE_ON_WEBSITE}${host ? ` at ${host}` : ''}. The agent opened the page after the change and saw it there.`;
    case 'not_live':
      return 'Not on your website yet. The change is saved, but the page still showed the earlier version when the agent last looked. The Fly team is looking into it.';
    default:
      return 'Saved, not on your website yet. The page takes a few minutes to refresh; the agent checks it and tells you the moment it shows.';
  }
}

/** The Done line for a website change, or null when the result carries no page check. */
export function siteDoneLine(result: unknown): string | null {
  const state = siteState(result);
  if (!state) return null;
  const r = (result ?? {}) as Record<string, unknown>;
  const check = r.site_check as Record<string, unknown>;
  const note = typeof r.note === 'string' && r.note.trim() ? ` ${r.note.trim()}` : '';
  return `${siteStandingLine(state, check.url ?? r.url)}${note}`;
}

/** The Done line for any recorded result both runtimes can rebuild, or null. */
export function doneLineFor(bridge: string, result: unknown): string | null {
  return changeDoneLine(bridge, result) ?? (bridge === 'website_feedback' ? siteDoneLine(result) : null);
}

/**
 * The first words of an executed card's Activity title, the console line and
 * the Done card: where the change stands, never "Done" for a change nobody
 * has seen yet. Null for work that is finished when it runs (a reply posted,
 * a name for post pictures saved), which keeps "Done".
 */
export function executedLead(bridge: string, result: unknown): string | null {
  if (isTrackedChange(bridge)) {
    const state = sendState(result);
    if (state === 'live') return 'Live on Google';
    if (state === 'replaced') return 'Replaced on Google';
    if (state === 'removed') return NO_LONGER_ON_GOOGLE;
    return 'Sent to Google';
  }
  if (bridge === 'website_feedback') {
    const state = siteState(result);
    if (state === 'live') return LIVE_ON_WEBSITE;
    if (state === 'pending') return 'Saved, not on your website yet';
    if (state === 'not_live') return 'Not on your website yet';
  }
  // A photo sent to Google is not yet a photo on the profile: Google shows it
  // once it has processed it, and the photo card on the home moves it from
  // "Not on your Google profile yet" to the profile's photos when it does
  // (Manu, QA 7fcda0e6, 25 Sep 26).
  if (bridge === 'photo_publish') return 'Sent to Google';
  // New service names go through Google's review like any other edit (the
  // area in service names, QA e952c067), so the line never says done first.
  if (bridge === 'services_rename') return 'Sent to Google';
  // An article is on the Fly site the moment its bridge has run (QA c4b950df).
  if (bridge === 'website_article_publish') return LIVE_ON_WEBSITE;
  // A review reply is on the profile the moment Google's reply PUT returns (review-replies/trail.ts).
  if (bridge === 'review_reply') return 'Live on Google';
  return null;
}

/** "Sent to Google: Add 3 more services to your Google profile", from a stored "Done: ..." title or the card's own. */
export function executedTitle(bridge: string, result: unknown, title: string): string {
  const bare = String(title ?? '').replace(/^(Done|Sent to Google|Live on Google|Replaced on Google|No longer on Google|Live on your website|Saved, not on your website yet|Not on your website yet): /, '');
  // An article reads as the article that went live, never as the card that
  // asked about it ("Approve your new article: ..."; QA c4b950df).
  if (bridge === 'website_article_publish') {
    const r = (result ?? {}) as Record<string, unknown>;
    const inner = (r.result ?? r) as Record<string, unknown>;
    const named = typeof inner.title === 'string' && inner.title.trim() ? inner.title.trim() : bare.replace(/^(Approve|Publishing) your new article: /, '');
    return `${LIVE_ON_WEBSITE}: ${named}`;
  }
  return `${executedLead(bridge, result) ?? 'Done'}: ${bare}`;
}

/**
 * How a closed card closes without work done (the done-work rule,
 * scripts/gbp-agent/checks/done-work.ts): handed to another card
 * (superseded), aged out (expired), or retired because its reason went
 * (agent_verified). None of those is a move, whatever status the card holds.
 */
export const CLOSED_WITHOUT_WORK = ['superseded', 'expired', 'agent_verified'];

/**
 * Cards the executor runs that record the owner's own answer or keep the
 * agent's books (the name on posts, what they want customers for, the post
 * days, a data refresh, the knowledge rebuild). They change nothing a
 * customer sees, so they are not moves. The older reviews clean-up's start
 * and stop are not either: the replies it posts go out on later passes.
 */
export const NOT_A_MOVE_BRIDGES = ['short_name', 'lead_focus', 'post_days', 'website_sync', 'location_context_build', 'review_backlog_start', 'review_backlog_stop'];

/** An executed card as both runtimes read it for the moved list and the automation measure. */
export interface MoveCard {
  status: string;
  executed_at: string | null;
  completion_source?: string | null;
  execution_payload: unknown;
  execution_result: unknown;
}

/** The bridge an executed card ran: the payload's, else the result's. */
export function moveBridge(card: Pick<MoveCard, 'execution_payload' | 'execution_result'>): string {
  const p = (card.execution_payload ?? {}) as Record<string, unknown>;
  const r = (card.execution_result ?? {}) as Record<string, unknown>;
  return String(p.bridge ?? r.bridge ?? '');
}

/**
 * Is this card a move: an executed card that changed something a customer
 * sees? The home's moved list (growth-state-shape.ts movedLines) shows
 * exactly these, and the automation measure (automation-rate.ts) counts
 * the agent's own among them, so the two never disagree (Anand, 27 Sep 26).
 */
export function isMove(card: MoveCard): boolean {
  if (card.status !== 'executed' || !card.executed_at) return false;
  if (card.completion_source && CLOSED_WITHOUT_WORK.includes(card.completion_source)) return false;
  if (NOT_A_MOVE_BRIDGES.includes(moveBridge(card))) return false;
  // The bridge decided there was nothing to do: not a change (done-work.ts).
  return !(card.execution_result as { skipped?: unknown } | null)?.skipped;
}

/**
 * Is the work done, by the verified-live rules (done-work.ts countsAsDone):
 * a profile edit once the verify pass saw it live on Google, a website
 * change once the published page shows it, and a write with no record to
 * wait on (a reply posted, a photo Google accepted) at once. Nothing the
 * bridge skipped.
 */
export function moveDone(result: unknown): boolean {
  const r = (result ?? {}) as Record<string, unknown>;
  if (r.skipped) return false;
  const verification = r.verification as { status?: unknown } | null | undefined;
  if (verification && typeof verification === 'object') return verification.status === 'live';
  const siteCheck = r.site_check as { status?: unknown } | null | undefined;
  if (siteCheck && typeof siteCheck === 'object') return siteCheck.status === 'live';
  return true;
}
